import { Box } from '@mui/material'
import { Fragment, useEffect, useRef } from 'react'

import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { type CounselTurnResponse } from '@/features/counsel'
import { FONT_FAMILY_MONO, UI_FONT_SIZE } from '@/shared/styles/typography'

// nagi の返事 1 つ。API の返事のうち面が読むもの (見立て・門・調べた結果・返事) と、送った発言・かかった秒
export type TurnInsight = Pick<
  CounselTurnResponse,
  'note' | 'can_move_on' | 'search' | 'sources' | 'reply'
> & {
  // 相談者が送った発言。写真だけのときは「（写真）」
  said: string
  // 送ってから返事が届くまで (画面で測る)
  seconds: number
}

interface Props {
  // 会話の順
  insights: TurnInsight[]
}

// ターミナルの見た目 (design-book ADR-0091。ADR-0012 の例外)。値は地との比 11.9:1、欄名と注釈は 6.4:1
const CONSOLE = {
  ground: colorTokens.dark.bgDefault,
  value: colorTokens.dark.fgDefault,
  comment: colorTokens.dark.fgMuted,
  axis: colorTokens.fg.muted,
  grid: colorTokens.fg.default,
} as const

// 段の高さ。不明は点を置かない
const STAGE_LEVEL: Record<string, number> = { '①': 1, '②': 2, '③': 3 }
const stageOf = (step: string) => step.charAt(0)
// 縦軸の目盛り (下から)。名前は trace の 段 の行に全部書く
const STAGE_TICKS = ['① 病気', '② 治療', '③ 大切なこと']

// nagi が選べる 4 つの手の定義 (prompt counsel の move の定義を処理の言葉で)
const MOVE_MEANING: Record<string, string> = {
  聞く: '主題をつかむ問いを置く',
  応える: 'その段の応え方で答える',
  確かめる: '変化の言葉が出た → 残りが無いかを聞く',
  移る: '「もう無い」→ code が出した次の段の問いを置く',
}

/**
 * 見立ての trace (design-book 04-apps/kakeru.md がんの相談・ADR-0091)。試作だけ・広い画面だけ。
 * 右端に高さいっぱいの黒い面。上に段 × 返事の番号の階段 (留まる)、下に返事ごとの trace (送られる)。
 * trace は 1 回の返事の中で起きた順: 発言 → code の門 → model の見立て → 手 → 調べたこと → 返事。色で分けない。
 */
export const CounselTracePane = ({ insights }: Props) => {
  const logRef = useRef<HTMLDivElement>(null)

  // 新しい回が来たら trace の末尾へ。面の中だけを送る (会話の側の送りに触らない)
  useEffect(() => {
    const log = logRef.current
    if (log) log.scrollTop = log.scrollHeight
  }, [insights.length])

  return (
    <Box
      component="aside"
      aria-label="nagi の見立ての trace"
      sx={{
        position: 'sticky',
        top: 0,
        height: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: CONSOLE.ground,
        color: CONSOLE.value,
        fontFamily: FONT_FAMILY_MONO,
        fontSize: UI_FONT_SIZE.tag,
        lineHeight: 1.6,
      }}
    >
      <Box sx={{ flex: 'none', px: 2, pt: 2, pb: 1 }}>
        <Box>
          <Box component="span" sx={{ color: CONSOLE.comment }}>
            ${' '}
          </Box>
          nagi trace
        </Box>
        <StageSteps insights={insights} />
      </Box>
      <Box
        ref={logRef}
        sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 2, pb: 2 }}
      >
        {insights.map((insight, i) => (
          <TurnTrace key={i} turn={i + 1} insight={insight} />
        ))}
      </Box>
    </Box>
  )
}

// 発言と返事は先頭だけ (全文は左の会話にある)
const HEAD_CHARS = 60
const head = (text: string) => {
  const chars = [...text]
  return chars.length > HEAD_CHARS
    ? `${chars.slice(0, HEAD_CHARS).join('')}…`
    : text
}

// 1 行 = 処理の段の名前 (tag)・note の欄名か手の値 (key)・値。注釈 (comment) は値の後ろに薄く
interface TraceRow {
  tag?: string
  key?: string
  value: string
  comment?: string
}

// 返事 1 回の trace。当たらない行 (体・つなぐ) は出さない
const TurnTrace = ({
  turn,
  insight,
}: {
  turn: number
  insight: TurnInsight
}) => {
  const { note } = insight
  const rows: TraceRow[] = [
    { tag: 'in', value: `「${head(insight.said)}」` },
    insight.can_move_on
      ? {
          tag: 'gate',
          key: 'open',
          value: '',
          comment:
            'code: 直前に ①② で確かめた → 移る を選べる型で model を呼ぶ',
        }
      : {
          tag: 'gate',
          key: 'closed',
          value: '',
          comment:
            'code: 直前に ①② で確かめていない → 移る の無い型で model を呼ぶ',
        },
    { tag: 'note', key: '段', value: note.step },
    { key: '主題', value: note.grasp || 'なし' },
    { key: '変化', value: note.shift ? `「${note.shift}」` : 'なし' },
  ]
  if (note.body.symptom)
    rows.push({
      key: '体',
      value: `${note.body.symptom} grade ${note.body.grade}`,
    })
  if (note.handoff !== 'なし') rows.push({ key: 'つなぐ', value: note.handoff })
  rows.push(
    {
      tag: 'move',
      key: note.move,
      value: '',
      comment: MOVE_MEANING[note.move],
    },
    {
      tag: 'search',
      value: insight.search
        ? `"${insight.search}" → 出典 ${insight.sources.length} 件`
        : 'なし',
    },
    { tag: 'out', value: `「${head(insight.reply)}」` }
  )

  return (
    <Box sx={{ mt: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box component="span">{`─ turn ${turn}`}</Box>
        <Box sx={{ flex: 1, borderTop: `1px solid ${CONSOLE.grid}` }} />
        <Box component="span" sx={{ color: CONSOLE.comment }}>
          {`${insight.seconds.toFixed(1)}s`}
        </Box>
      </Box>
      {rows.map((row, i) => (
        <Box
          key={i}
          sx={{
            display: 'grid',
            gridTemplateColumns: `${TAG_WIDTH} ${KEY_WIDTH} minmax(0, 1fr)`,
            pl: '1ch',
          }}
        >
          <Box component="span" sx={{ color: CONSOLE.comment }}>
            {row.tag}
          </Box>
          {row.key === undefined ? (
            <Box component="span" sx={{ gridColumn: '2 / 4' }}>
              {row.value}
            </Box>
          ) : (
            <>
              <Box
                component="span"
                // note の欄名は薄く、門と手の値は濃く
                sx={{
                  color:
                    row.tag === 'note' || !row.tag
                      ? CONSOLE.comment
                      : undefined,
                }}
              >
                {row.key}
              </Box>
              <Box component="span">
                {row.value}
                {row.comment && (
                  <Box component="span" sx={{ color: CONSOLE.comment }}>
                    {row.comment}
                  </Box>
                )}
              </Box>
            </>
          )}
        </Box>
      ))}
    </Box>
  )
}

// 処理の段の名前の列 (最長の search = 6 字 + 余白) と、欄名・手の列 (最長の 確かめる = 全角 4 字 + 余白)
const TAG_WIDTH = '8ch'
const KEY_WIDTH = '10ch'

const CHART = {
  width: 408,
  height: 144,
  left: 92,
  right: 8,
  top: 24,
  bottom: 32,
} as const
// 返事が少ないうちに 2 点で横幅いっぱいに伸ばさない
const MIN_X_SPAN = 8
// 返事がこれより多ければ、横軸の目盛りは 1 と 5 の倍数だけ
const ALL_TICKS_MAX = 12

// 段 × 返事の番号の階段 (1 系列。線 2px・点 8px に地の色の 2px の輪)。縦軸・横軸に名前。点に指を置くとその回が出る
const StageSteps = ({ insights }: Props) => {
  const span = Math.max(insights.length, MIN_X_SPAN)
  const plotWidth = CHART.width - CHART.left - CHART.right
  const plotHeight = CHART.height - CHART.top - CHART.bottom
  const plotBottom = CHART.top + plotHeight
  const x = (i: number) => CHART.left + 8 + (i / (span - 1)) * (plotWidth - 16)
  const y = (level: number) =>
    CHART.top + 6 + ((3 - level) / 2) * (plotHeight - 18)
  const points = insights.map((insight, i) => {
    const level = STAGE_LEVEL[stageOf(insight.note.step)]
    return level ? { i, x: x(i), y: y(level), insight } : null
  })
  // 段が分からない回で線を切る。段が変わる所は、次の回で縦に上がる (前の段のまま次の回まで進む)
  let path = ''
  points.forEach((p, i) => {
    if (!p) return
    path += points[i - 1] ? ` H ${p.x} V ${p.y}` : ` M ${p.x} ${p.y}`
  })
  const ticks = insights
    .map((_, i) => i + 1)
    .filter((n) => insights.length <= ALL_TICKS_MAX || n === 1 || n % 5 === 0)

  return (
    <Box
      component="svg"
      viewBox={`0 0 ${CHART.width} ${CHART.height}`}
      role="img"
      aria-label="段の移り変わり (縦軸 段・横軸 返事の番号)"
      sx={{
        display: 'block',
        width: '100%',
        height: 'auto',
        mt: 1,
        fontFamily: FONT_FAMILY_MONO,
      }}
    >
      <text x={0} y={10} fontSize={UI_FONT_SIZE.micro} fill={CONSOLE.comment}>
        段 = 相談者が今話している主題
      </text>
      {[1, 2, 3].map((level) => (
        <Fragment key={level}>
          <line
            x1={CHART.left}
            x2={CHART.width - CHART.right}
            y1={y(level)}
            y2={y(level)}
            stroke={CONSOLE.grid}
            strokeWidth={1}
          />
          <text
            x={CHART.left - 8}
            y={y(level)}
            textAnchor="end"
            dominantBaseline="central"
            fontSize={UI_FONT_SIZE.micro}
            fill={CONSOLE.comment}
          >
            {STAGE_TICKS[level - 1]}
          </text>
        </Fragment>
      ))}
      <path
        d={`M ${CHART.left} ${CHART.top} V ${plotBottom} H ${
          CHART.width - CHART.right
        }`}
        fill="none"
        stroke={CONSOLE.axis}
        strokeWidth={1}
      />
      {ticks.map((n) => (
        <text
          key={n}
          x={x(n - 1)}
          y={plotBottom + 14}
          textAnchor="middle"
          fontSize={UI_FONT_SIZE.micro}
          fill={CONSOLE.comment}
        >
          {n}
        </text>
      ))}
      <text
        x={CHART.width - CHART.right}
        y={CHART.height - 2}
        textAnchor="end"
        fontSize={UI_FONT_SIZE.micro}
        fill={CONSOLE.comment}
      >
        返事の番号 →
      </text>
      <path
        d={path}
        fill="none"
        stroke={CONSOLE.value}
        strokeWidth={2}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      {points.map(
        (p) =>
          p && (
            <circle
              key={p.i}
              cx={p.x}
              cy={p.y}
              r={4}
              fill={CONSOLE.value}
              stroke={CONSOLE.ground}
              strokeWidth={2}
            >
              <title>
                {[
                  p.i + 1,
                  p.insight.note.step,
                  p.insight.note.move,
                  p.insight.note.grasp,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </title>
            </circle>
          )
      )}
    </Box>
  )
}
